import {
  ButtonTargetKind,
  Button,
  ButtonRole,
  ButtonSize,
} from "@components/Button";
import { Checkbox } from "@components/Checkbox";
import { ErrorMessage } from "@components/ErrorMessage";
import { Loading } from "@components/LoadingIndicator";
import { TextInput } from "@components/TextInput";
import { useEmbedMode } from "@components/donate/DonateV3/EmbedModeContext";
import { validateNameEmail } from "@components/donate/DonateV3/PaymentProcess/validators";
import { DonateFormType } from "@components/donate/DonateV3/types";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import React, { useEffect, useMemo, useState } from "react";
import { UseFormSetError } from "react-hook-form";

import { CurrencyValue } from "@every.org/common/src/codecs/currency";
import {
  PaymentSourceResponse,
  Uuid,
} from "@every.org/common/src/codecs/entities";
import { Location } from "@every.org/common/src/codecs/location";
import { SafeInt } from "@every.org/common/src/codecs/number";
import { DonationFrequency } from "@every.org/common/src/entity/types";
import { CustomEvent } from "@every.org/common/src/helpers/analytics";
import { getUserFullName } from "@every.org/common/src/helpers/username";
import {
  attachPaymentSourceRouteSpec,
  createCardSetupIntentRouteSpec,
  createCardPaymentIntentRouteSpec,
} from "@every.org/common/src/routes/donate";

import { TextInputLabelText } from "src/components/InputContainer";
import { useLoggedInOrGuestUserOrUndefined } from "src/context/AuthContext/hooks";
import { isLoggedInUser } from "src/context/AuthContext/selectors";
import { getMessageForError } from "src/errors";
import { StripeClientError } from "src/errors/StripeClientError";
import { InputWithLabelContainer } from "src/pages/Fundraiser/EditForm/styles";
import { colorCssVars } from "src/theme/color";
import { roundedBorder } from "src/theme/common";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  spacing,
  verticalStackCss,
  horizontalStackCss,
} from "src/theme/spacing";
import { TextSize, textSizeCss, TextCssVar } from "src/theme/text";
import { trackEvent } from "src/utility/analytics";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";
import { useStripePromise } from "src/utility/stripe";
import { getWindow } from "src/utility/window";

const StyledTextInputLabelText = styled(TextInputLabelText)`
  ${textSizeCss[TextSize.s]};
  ${cssForMediaSize({ min: MediaSize.MEDIUM, css: textSizeCss[TextSize.s] })}
`;

const AddCcContainer = styled.div`
  .cardInput {
    border: 1px solid var(${colorCssVars.dividerSoft});
    ${roundedBorder};
    background: var(${colorCssVars.input.background.default});
    padding: ${spacing.xs};
    ${textSizeCss[TextSize.s]};
    color: var(${colorCssVars.text.body});
    height: calc(
      var(${TextCssVar.SIZE}) * var(${TextCssVar.LINE_HEIGHT}) +
        (2 * ${spacing.xs})
    ); /* Stripe card element jumps in size if we don't set this and use base object style fontSize */
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const paymentElementContainerCss = css`
  width: 392px;
  ${cssForMediaSize({
    max: MediaSize.MEDIUM_SMALL,
    css: css`
      width: 100%;
    `,
  })}
`;

export enum COLLECT_ZIP {
  NO = "NO",
  OPTIONAL = "OPTIONAL",
  REQUIRED = "REQUIRED",
}

interface PaymentSourceCallback {
  (paymentSource: PaymentSourceResponse): void;
}

export interface AddCreditCardProps {
  handleConnectBankAccount: () => void;
  onPaymentSourceAdded: PaymentSourceCallback;
  cancellable: boolean;
  onAddCancelled: () => void;
  firstName?: string;
  lastName?: string;
  email?: string;
  isAddingCard?: boolean;
  collectZip?: COLLECT_ZIP;
  setFormError?: UseFormSetError<DonateFormType>;
  /**
   * Called whenever the donor edits the Payment Element, so a stale
   * DONATE_STRIPE_ERROR/DONATE_FORM_ERROR from a previous failed attempt
   * doesn't linger once they've actually changed something.
   */
  clearFormError?: () => void;
  /**
   * Which Stripe flow this instance uses - set explicitly by each caller
   * rather than inferred:
   * - "setup": a SetupIntent-based "Add card" button of its own - creates
   *   an unconfirmed SetupIntent, confirms it client-side, then attaches
   *   the resulting PaymentMethod via attachPaymentSourceRouteSpec. Used by
   *   the standalone "My Giving" payment methods page (PaymentPage.tsx).
   * - "payment": no button of its own - registers a function on
   *   createNewCardPaymentIntentRef that creates and confirms a
   *   manual-capture PaymentIntent for the entered card, for
   *   submitDonation to call right before /donate. Used by the donate flow
   *   (SelectPaymentSource.tsx), for every donor/frequency combination.
   */
  mode: "setup" | "payment";
  /**
   * Required when mode is "payment": the amount (in the currency's
   * smallest denomination, e.g. cents) to be charged. Needed to initialize
   * Stripe Elements in deferred "payment" mode so PaymentElement can render
   * before any PaymentIntent exists.
   */
  amountInMinDenom?: number;
  /**
   * Required when mode is "payment": needed to create the PaymentIntent
   * when createNewCardPaymentIntentRef's function is called.
   */
  toNonprofitId?: Uuid;
  value?: CurrencyValue;
  frequency?: DonationFrequency;
  /**
   * Donation tip to every.org, in the currency's smallest denomination -
   * folded into the PaymentIntent's amount via getChargePaidAmount, same as
   * donate()'s tipAmount.
   */
  tipAmount?: SafeInt;
  /**
   * Giving credit to consume, in the currency's smallest denomination -
   * same as donate()'s creditAmount, needed so the PaymentIntent's amount
   * matches what /donate will actually charge.
   */
  creditAmount?: SafeInt;
  /**
   * mode: "payment" only: registered (while mounted, and kept up to date
   * with the latest amount/tip/credit props) with a function that creates a
   * manual-capture PaymentIntent for the currently-entered card and
   * confirms it client-side. submitDonation calls this right before
   * creating the donation, so the PaymentIntent it creates always reflects
   * the amount at submit time rather than whenever the donor typed in
   * their card.
   */
  createNewCardPaymentIntentRef?: React.MutableRefObject<
    | (() => Promise<{ paymentMethodId: string; paymentIntentId: string }>)
    | undefined
  >;
  /**
   * The donor's address, if the donate form's own Address field is shown
   * and they've entered one (see DonorInfo/AddressInput's `location` form
   * field) - used to reconcile that field against the Payment Element's own
   * built-in address/ZIP collection (see isAddressRequired).
   */
  location?: Location;
  /**
   * Whether the donate form's own Address field is being shown *and*
   * required (i.e. COLLECT_ADDRESS.REQUIRED and not already known - see
   * useCollectAddress). When true, the Payment Element's own country/ZIP
   * fields are redundant with that required field, so they're hidden and
   * `location` is sent to Stripe as the billing address instead. When
   * false but an address was still entered (the field is shown but
   * optional), the Payment Element's own country/ZIP fields stay visible
   * but get pre-filled from `location` so the donor doesn't have to retype
   * it.
   */
  isAddressRequired?: boolean;
}

/**
 * Maps the donate form's own Address field (Google Places autocomplete, see
 * DonorInfo/AddressInput) to the shape Stripe's billing_details.address
 * expects. Only meaningful once geocoding has resolved a `geolocation` -
 * before that (or if the donor never picked a real suggestion), there's
 * nothing to send.
 */
function billingAddressFromLocation(location?: Location) {
  if (!location?.geolocation) {
    return undefined;
  }
  const { streetAddress, city, state, country, postalCode } =
    location.geolocation;
  return {
    line1: streetAddress || undefined,
    city: city || undefined,
    state: state || undefined,
    country: country || undefined,
    postal_code: postalCode || undefined,
  };
}

/**
 * For `mode: "setup"` ("add card"): its own button creates an unconfirmed
 * SetupIntent, confirms it client-side, then attaches the resulting
 * PaymentMethod via attachPaymentSourceRouteSpec.
 *
 * For `mode: "payment"` (the donate flow, any donor/frequency): no button
 * of its own - just registers a function on createNewCardPaymentIntentRef
 * that creates and confirms a manual-capture PaymentIntent for the entered
 * card, for submitDonation to call right before /donate.
 */
function AddCcForm({
  mode,
  onPaymentSourceAdded,
  handleConnectBankAccount: _handleConnectBankAccount,
  cancellable,
  onAddCancelled,
  firstName,
  lastName,
  email,
  isAddingCard: _isAddingCard,
  collectZip = COLLECT_ZIP.NO,
  setFormError,
  clearFormError,
  toNonprofitId,
  value,
  frequency,
  tipAmount,
  creditAmount,
  createNewCardPaymentIntentRef,
  location,
  isAddressRequired,
}: AddCreditCardProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [zipCode, setZipCode] = useState<string>();
  const [saveForFutureUse, setSaveForFutureUse] = useState(false);
  const loggedInOrGuestUser = useLoggedInOrGuestUserOrUndefined();
  const embedMode = useEmbedMode();
  // In payment mode the Payment Element collects the postal code itself
  // (address fields default to "auto"; when the donate form's Address field
  // covers it, that field provides it instead) - a separate legacy ZIP
  // input would both duplicate the field on screen and make confirmPayment
  // reject the extra billing_details value.
  const showCollectZipInput = mode === "setup" && collectZip !== COLLECT_ZIP.NO;
  const isRecurring =
    frequency !== undefined && frequency !== DonationFrequency.ONCE;
  // Guests can never opt into saving a card on a one-time donation - only
  // logged-in donors get the checkbox (see the "save" checkbox below).
  // Recurring donations save regardless of login status, but that's a
  // separate, mandatory rule (setup_future_usage forced server-side, no
  // checkbox involved) - see createCardPaymentIntent.
  // The embedded flow is guest-only: treat logged-in users as guests so
  // cards are never saved from a partner site.
  const isGuestDonor = embedMode || !isLoggedInUser(loggedInOrGuestUser);
  // Only sent to Stripe when the donate form's own Address field is
  // required - in that case the Payment Element's own country/ZIP fields
  // are hidden (see the PaymentElement options below), so this is the only
  // source of a billing address for the PaymentMethod. Memoized (rather
  // than a plain const) so it has a stable reference for the
  // createNewCardPaymentIntentRef effect's dependency array below.
  const billingAddress = useMemo(
    () =>
      isAddressRequired ? billingAddressFromLocation(location) : undefined,
    [isAddressRequired, location]
  );
  // Hiding the Payment Element's country/ZIP fields ("never") makes Stripe
  // *require* both values in confirmParams' billing_details.address - so
  // only do it when the donate form's Address field actually produced both.
  // A partial geocode (no postal code is common outside the US, or when the
  // donor never picked a real suggestion) would otherwise make
  // confirmPayment fail with no field for the donor to fill in.
  const addressCoveredByForm = Boolean(
    billingAddress?.country && billingAddress?.postal_code
  );
  // Covered: the donate form's own Address field already collected this, so
  // hide the Payment Element's redundant country/ZIP fields entirely -
  // billingAddress above is sent instead. Otherwise: leave the Payment
  // Element's fields visible (the donor can still correct them) but
  // pre-fill them from whatever was already typed into the Address field.
  const paymentElementAddressFieldOption = addressCoveredByForm
    ? ({ country: "never", postalCode: "never" } as const)
    : undefined;
  const paymentElementAddressDefaultValues =
    !addressCoveredByForm && location?.geolocation
      ? {
          country: location.geolocation.country,
          postal_code: location.geolocation.postalCode,
        }
      : undefined;

  // Keep Elements' deferred setup_future_usage in sync with what
  // createCardPaymentIntent will actually set server-side once this card is
  // submitted (recurring always off_session; a one-time, logged-in donor's
  // opt-in checkbox otherwise) - confirmPayment throws if the real
  // PaymentIntent's setup_future_usage doesn't match what Elements was told
  // to expect (same requirement as the Elements options' captureMethod).
  // The Elements-level `setupFutureUsage` option below only covers the
  // known-at-mount recurring case; this effect covers the checkbox toggling
  // after mount.
  useEffect(() => {
    if (mode !== "payment" || !elements) {
      return;
    }
    elements.update({
      setupFutureUsage:
        isRecurring || (saveForFutureUse && !isGuestDonor)
          ? "off_session"
          : null,
    });
  }, [mode, elements, isRecurring, saveForFutureUse, isGuestDonor]);

  useEffect(() => {
    trackEvent("Viewed add credit card form", {});
  });

  useEffect(() => {
    if (mode !== "payment" || !createNewCardPaymentIntentRef) {
      return;
    }
    if (!toNonprofitId || !value || !frequency) {
      createNewCardPaymentIntentRef.current = undefined;
      return;
    }
    createNewCardPaymentIntentRef.current = async () => {
      const window = getWindow();
      if (!stripe || !elements || !window) {
        throw new Error(
          "Payment failed to load. Please refresh the page and try again."
        );
      }
      // Only enforceable when the legacy ZIP input is actually on screen
      // (setup mode) - in payment mode the Payment Element collects the
      // postal code itself.
      if (
        showCollectZipInput &&
        collectZip === COLLECT_ZIP.REQUIRED &&
        !zipCode
      ) {
        throw new Error("Please enter your ZIP code.");
      }
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new StripeClientError(submitError, "Invalid card details.");
      }
      const { clientSecret, paymentIntentId } = await queryApi(
        createCardPaymentIntentRouteSpec,
        {
          body: {
            toNonprofitId,
            value,
            frequency,
            tipAmount,
            creditAmount,
            // Server-side ignores this for guest donors regardless - see
            // createCardPaymentIntentForNewDonation.
            saveForFutureUse,
            zip: zipCode,
          },
          routeTokens: {},
          queryParams: {},
        },
        { disableUnauthenticatedToast: true }
      );
      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                email,
                name: getUserFullName({ firstName, lastName }) || undefined,
                // Only send an address when the Payment Element's own
                // address fields are hidden ("never") - Stripe rejects
                // billing_details values for fields the Payment Element is
                // collecting itself.
                address: addressCoveredByForm ? billingAddress : undefined,
              },
            },
            // Required by Stripe's types even though the PaymentIntent is
            // card-only (payment_method_types: ["card"], see
            // createCardPaymentIntent) and confirmation is handled in an
            // iframe - this should never actually be used.
            return_url: window.location.href,
          },
          redirect: "if_required",
        });
      if (confirmError) {
        throw new StripeClientError(
          confirmError,
          "Unable to charge this card. Please try again."
        );
      }
      if (!paymentIntent || !paymentIntent.payment_method) {
        throw new Error("Unable to charge this card. Please try again.");
      }
      trackEvent(CustomEvent.PAYMENT_ADDED, { type: "card", zip: !!zipCode });
      const paymentMethod = paymentIntent.payment_method;
      return {
        paymentMethodId:
          typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id,
        paymentIntentId,
      };
    };
    return () => {
      createNewCardPaymentIntentRef.current = undefined;
    };
  }, [
    mode,
    createNewCardPaymentIntentRef,
    stripe,
    elements,
    collectZip,
    showCollectZipInput,
    zipCode,
    email,
    firstName,
    lastName,
    toNonprofitId,
    value,
    frequency,
    tipAmount,
    creditAmount,
    billingAddress,
    addressCoveredByForm,
    saveForFutureUse,
  ]);

  const handleSubmit = async () => {
    const window = getWindow();
    if (!stripe || !elements || !window) {
      logger.fatal({
        message:
          "Stripe or stripe-elements not injected, people cannot donate, investigate immediately",
        data: { stripe, elements },
      });
      setError(
        "Payment failed to load. Please refresh the page and try again."
      );
      return;
    }
    if (
      setFormError &&
      !validateNameEmail({
        firstName,
        lastName,
        email,
        setError: setFormError,
      })
    ) {
      return;
    }

    setSubmitting(true);

    // Stripe's own PaymentElement collects and validates zip/address itself
    // (fields.billingDetails.address defaults to "auto") - this flow is
    // logged-in only (see createCardSetupIntentRoute), so there's no
    // donate-form Address/ZIP field to reconcile against and no guest user
    // to create.
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || "Invalid payment details.");
      setSubmitting(false);
      return;
    }

    try {
      const { clientSecret } = await queryApi(createCardSetupIntentRouteSpec, {
        body: {},
        routeTokens: {},
        queryParams: {},
      });
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email,
              name: getUserFullName({ firstName, lastName }) || undefined,
              // See the payment-mode confirmParams above - only send an
              // address when the Payment Element's own fields are hidden.
              address: addressCoveredByForm ? billingAddress : undefined,
            },
          },
          // Required by Stripe's types even though the SetupIntent is
          // card-only and confirmation is handled in an iframe - this
          // should never actually be used.
          return_url: window.location.href,
        },
        redirect: "if_required",
      });
      if (confirmError || !setupIntent) {
        setError(
          confirmError?.message ||
            "Unable to verify this card. Please try again."
        );
        setSubmitting(false);
        return;
      }
      const paymentSource = await queryApi(attachPaymentSourceRouteSpec, {
        body: {
          setupIntentId: setupIntent.id,
          zip: zipCode,
        },
        routeTokens: {},
        queryParams: {},
      });
      trackEvent(CustomEvent.PAYMENT_ADDED, { type: "card", zip: !!zipCode });
      onPaymentSourceAdded(paymentSource);
      setSubmitting(false);
    } catch (e) {
      if (e instanceof Error) {
        setError(getMessageForError(e));
      }
      setSubmitting(false);
    }
  };

  return (
    <AddCcContainer>
      <div css={[verticalStackCss.xxl]}>
        <div css={verticalStackCss.l}>
          <InputWithLabelContainer>
            <PaymentElement
              css={paymentElementContainerCss}
              onChange={() => {
                // A stale error from a previous failed attempt shouldn't
                // linger once the donor has actually changed something -
                // clears both the local (setup flow) error and, for the
                // donate flow, DONATE_STRIPE_ERROR/DONATE_FORM_ERROR.
                setError(undefined);
                clearFormError && clearFormError();
              }}
              options={{
                layout: "tabs",
                // name/email are collected via our own form fields and
                // passed in confirmParams.payment_method_data.billing_details
                // below, so don't have the Payment Element collect them too.
                // address is left at its default ("auto") unless the
                // donate form's own Address field makes it redundant - see
                // paymentElementAddressFieldOption above.
                fields: {
                  billingDetails: {
                    name: "never",
                    email: "never",
                    ...(paymentElementAddressFieldOption
                      ? { address: paymentElementAddressFieldOption }
                      : {}),
                  },
                },
                // Disable Link (adds a "Save my information" checkbox) and
                // the Apple Pay/Google Pay express buttons - wallets are
                // offered via the separate PAYMENT_REQUEST payment option
                // tab, not inline in the card fields.
                wallets: {
                  link: "never",
                  applePay: "never",
                  googlePay: "never",
                },
                // Suppress Stripe's own future-usage disclaimer ("By
                // providing your card information...") - our own "Save this
                // card for future donations" checkbox (and its copy) is the
                // actual consent UI here.
                terms: { card: "never" },
                ...(paymentElementAddressDefaultValues
                  ? {
                      defaultValues: {
                        billingDetails: {
                          address: paymentElementAddressDefaultValues,
                        },
                      },
                    }
                  : {}),
              }}
            />
          </InputWithLabelContainer>
          {mode === "payment" && !isRecurring && !isGuestDonor && (
            <Checkbox
              data-tname="saveCardForFutureUse"
              checked={saveForFutureUse}
              onClick={() => setSaveForFutureUse((prev) => !prev)}
              css={horizontalStackCss.xs}
            >
              <span css={[horizontalStackCss.xs, { alignItems: "center" }]}>
                Save this card for future donations
              </span>
            </Checkbox>
          )}
          {showCollectZipInput && (
            <div css={horizontalStackCss.l}>
              <InputWithLabelContainer>
                <StyledTextInputLabelText>ZIP code</StyledTextInputLabelText>
                <TextInput
                  placeholder="12345"
                  maxLength={12} // Usually only 5, but some people in US use 9 and other countries can be up to 12
                  css={{ width: "86px" }}
                  className="rr-block"
                  data-tname="cardZip"
                  name="postal"
                  value={zipCode}
                  onChange={(v) => setZipCode(v.target.value)}
                />
              </InputWithLabelContainer>
            </div>
          )}
        </div>
        {mode === "setup" ? (
          <React.Fragment>
            {error && <ErrorMessage text={error} />}
            <ButtonContainer>
              {cancellable && (
                <Button
                  role={ButtonRole.TEXT_ONLY}
                  data-tname="cancelCardSubmit"
                  size={ButtonSize.SMALL}
                  onClick={{
                    kind: ButtonTargetKind.FUNCTION,
                    action: onAddCancelled,
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                data-tname="addCardSubmit"
                data-bloom-block
                role={ButtonRole.PRIMARY}
                size={ButtonSize.SMALL}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: handleSubmit,
                }}
                submitting={submitting}
                disabled={!stripe || submitting}
                css={css`
                  margin-left: auto;
                `}
              >
                Add card
              </Button>
            </ButtonContainer>
          </React.Fragment>
        ) : (
          cancellable && (
            <ButtonContainer>
              <Button
                role={ButtonRole.TEXT_ONLY}
                data-tname="cancelCardSubmit"
                size={ButtonSize.SMALL}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: onAddCancelled,
                }}
              >
                Cancel
              </Button>
            </ButtonContainer>
          )
        )}
      </div>
    </AddCcContainer>
  );
}

/**
 * Component to add or use a credit card via Stripe PaymentElement, mounted
 * in Stripe's "deferred" Elements mode - no PaymentIntent/SetupIntent needs
 * to exist yet for the card fields to render.
 *
 * - `mode: "setup"` ("add card"): its own button creates an unconfirmed
 *   SetupIntent, confirms it client-side, then attaches the resulting
 *   PaymentMethod via attachPaymentSourceRouteSpec.
 * - `mode: "payment"` (the donate flow, any donor/frequency): registers a
 *   function (see createNewCardPaymentIntentRef) that creates a
 *   manual-capture PaymentIntent and confirms it - called by submitDonation
 *   right before /donate, so it always uses the current amount rather than
 *   whatever was current the last time the donor's card details changed.
 */
export const AddCreditCard: React.FCC<AddCreditCardProps> = (props) => {
  const { mode, amountInMinDenom, frequency } = props;
  const stripePromise = useStripePromise();
  const isRecurring =
    frequency !== undefined && frequency !== DonationFrequency.ONCE;

  if (!stripePromise) {
    return <Loading />;
  }

  if (mode === "payment") {
    if (!amountInMinDenom) {
      logger.fatal({
        message:
          "AddCreditCard rendered in payment mode but no amountInMinDenom",
      });
      return <Loading />;
    }
    return (
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          amount: amountInMinDenom,
          currency: "usd",
          paymentMethodTypes: ["card"],
          // Without this, Stripe shows its own "Save my information"
          // checkbox because a customer is attached to the PaymentIntent
          // (see createCardPaymentIntentForNewDonation) - AddCcForm renders
          // its own checkbox instead. Recurring always saves (off_session);
          // a one-time donation starts unchecked. Either way, this must
          // match what createCardPaymentIntent will actually set
          // server-side or confirmPayment throws - AddCcForm's own effect
          // keeps this in sync afterward as the checkbox is toggled (this
          // initial value can't react to that, since it lives above the
          // checkbox's own state).
          setupFutureUsage: isRecurring ? "off_session" : null,
          // Must match createCardPaymentIntent's capture_method: "manual" -
          // confirmPayment throws otherwise. The PaymentIntent (created via
          // createCardPaymentIntentRouteSpec when createNewCardPaymentIntentRef's
          // function is called) authorizes only; payWithStripe captures it
          // once the donation exists.
          captureMethod: "manual",
        }}
      >
        <AddCcForm {...props} />
      </Elements>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ mode: "setup", currency: "usd", paymentMethodTypes: ["card"] }}
    >
      <AddCcForm {...props} />
    </Elements>
  );
};
